import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { initials, statusBadge, plural, ASSET_ICONS, ASSET_LABELS, formatDate } from '../utils';
import TransferModal from '../components/TransferModal';

export default function EmployeeDetail() {
  const { id } = useParams();
  const { withAuth, user } = useAuth();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transferAsset, setTransferAsset] = useState(null);

  const canEdit = ['it_admin', 'it_staff', 'supervisor'].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      await withAuth(async () => {
        const res = await api.getEmployee(id);
        setEmployee(res);
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id, withAuth]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="empty-state"><div className="spinner" /></div>;
  if (!employee) return <div className="empty-state">Employee not found</div>;

  const name = `${employee.first_name} ${employee.last_name}`;

  return (
    <div>
      <div style={{ padding: '20px 28px', borderBottom: '2px solid var(--accent)', background: 'var(--bg-1)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="btn btn-sm" onClick={() => navigate('/employees')}>← Back</button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 18, fontWeight: 600 }}>{name}</h1>
          <p style={{ color: 'var(--text-2)', fontSize: 13, marginTop: 2 }}>{employee.job_title}</p>
        </div>
        {employee.employment_status === 'terminated' && (
          <span className="badge badge-red">Terminated</span>
        )}
      </div>

      <div style={{ padding: 28, display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 20 }}>
        {/* Profile card */}
        <div>
          <div className="card" style={{ padding: 24, textAlign: 'center', marginBottom: 16 }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%', margin: '0 auto 16px',
              background: 'var(--accent-dim)', border: '1px solid var(--accent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 22, fontWeight: 600, color: 'var(--accent-text)',
            }}>
              {initials(name)}
            </div>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>{name}</div>
            <div style={{ color: 'var(--text-2)', fontSize: 14, marginBottom: 16 }}>{employee.job_title}</div>

            <div style={{ textAlign: 'left', borderTop: '1px solid var(--border)', paddingTop: 16 }}>
              {[
                { label: 'Employee ID', value: employee.employee_number || employee.workday_employee_id },
                { label: 'Email', value: employee.email || '—' },
                { label: 'Location', value: employee.home_location_name || '—' },
                { label: 'Hierarchy', value: employee.hierarchy_group || '—' },
                { label: 'Cost Center', value: employee.cost_center || '—' },
                { label: 'Route', value: employee.route_number || '—' },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14 }}>
                  <span style={{ color: 'var(--text-2)' }}>{label}</span>
                  <span style={{ fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Termination flag */}
          {employee.terminationFlag && (
            <div style={{
              background: 'var(--red-dim)', border: '1px solid var(--red)',
              borderRadius: 'var(--radius-md)', padding: 16,
            }}>
              <div style={{ fontWeight: 600, color: 'var(--red)', marginBottom: 8 }}>⚠ Termination Flag</div>
              <div style={{ fontSize: 14, color: 'var(--text-1)', marginBottom: 4 }}>
                {employee.terminationFlag.assets_outstanding} {plural(employee.terminationFlag.assets_outstanding, 'device')} pending return
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
                Flagged: {formatDate(employee.terminationFlag.detected_at)}
              </div>
            </div>
          )}
        </div>

        {/* Assets */}
        <div>
          <div className="card">
            <div className="card-header">
              <h3>Assigned Devices ({employee.assets?.length || 0})</h3>
            </div>
            {!employee.assets?.length
              ? <div className="empty-state">No devices assigned</div>
              : (
                <table>
                  <thead>
                    <tr>
                      <th>Device</th>
                      <th>Tag</th>
                      <th>Status</th>
                      <th>Location</th>
                      {canEdit && <th></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {employee.assets.map(a => {
                      const sb = statusBadge(a.status);
                      return (
                        <tr key={a.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/assets/${a.id}`)}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: 18 }}>{ASSET_ICONS[a.asset_type]}</span>
                              <div>
                                <div style={{ fontWeight: 500 }}>{a.display_name}</div>
                                <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{ASSET_LABELS[a.asset_type]}</div>
                              </div>
                            </div>
                          </td>
                          <td><span className="mono">{a.asset_tag}</span></td>
                          <td><span className={`badge ${sb.className}`}>{sb.label}</span></td>
                          <td style={{ color: 'var(--text-2)', fontSize: 14 }}>{a.location_name || '—'}</td>
                          {canEdit && (
                            <td onClick={e => e.stopPropagation()}>
                              <button className="btn-icon" title="Transfer" onClick={() => setTransferAsset(a)}>⇄</button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )
            }
          </div>
        </div>
      </div>

      {transferAsset && (
        <TransferModal
          asset={transferAsset}
          onClose={() => setTransferAsset(null)}
          onSuccess={() => { setTransferAsset(null); load(); }}
        />
      )}
    </div>
  );
}
